import { useState } from "react";
import ResourcePage from "@/components/common/ResourcePage";
import { useMeta } from "@/context/MetaContext";
import { useAuth } from "@/context/AuthContext";
import { money, num } from "@/lib/api";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftRight, AlertTriangle } from "lucide-react";

export default function Inventory() {
  const { options, outletOptions, outletName } = useMeta();
  const { user, can } = useAuth();
  const isSuper = user?.role === "super_admin" || user?.role === "finance";
  const [stockItem, setStockItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tx, setTx] = useState({ type: "in", qty: "", reason: "", reference: "" });
  const [busy, setBusy] = useState(false);

  const fields = [
    { name: "name", label: "Item Name", span: 1, required: true },
    { name: "sku", label: "SKU", span: 1 },
    { name: "category", label: "Category", type: "select", span: 1, options: options("inventory_category"), required: true },
    { name: "unit", label: "Unit", type: "select", span: 1, options: options("unit"), required: true },
    ...(isSuper ? [{ name: "outlet_id", label: "Outlet", type: "select", span: 1, options: outletOptions, required: true }] : []),
    { name: "warehouse", label: "Warehouse", span: 1, default: "Main Store" },
    { name: "opening_stock", label: "Opening Stock", type: "number", span: 1, step: "0.01", default: 0 },
    { name: "reorder_level", label: "Reorder Level", type: "number", span: 1, step: "0.01", default: 0 },
    { name: "cost_price", label: "Cost Price", type: "number", span: 1, step: "0.01", default: 0 },
    { name: "expiry_date", label: "Expiry Date", type: "date", span: 1 },
  ];

  const low = (r) => Number(r.reorder_level) > 0 && Number(r.current_stock) <= Number(r.reorder_level);

  const columns = [
    { key: "name", label: "Item", sortable: true, render: (r) => (
      <div className="flex items-center gap-2">
        <span className="font-medium">{r.name}</span>
        {low(r) && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
      </div>
    ) },
    { key: "sku", label: "SKU", render: (r) => <span className="tabular text-xs text-muted-foreground">{r.sku}</span> },
    { key: "category", label: "Category" },
    ...(isSuper ? [{ key: "outlet_id", label: "Outlet", render: (r) => outletName(r.outlet_id) }] : []),
    { key: "current_stock", label: "Stock", sortable: true, className: "text-right", cellClassName: "text-right",
      render: (r) => <span className={low(r) ? "font-semibold text-amber-600 tabular" : "tabular"}>{num(r.current_stock)} {r.unit}</span> },
    { key: "reorder_level", label: "Reorder", className: "text-right", cellClassName: "tabular text-right", render: (r) => num(r.reorder_level) },
    { key: "cost_price", label: "Cost", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(r.cost_price) },
    { key: "value", label: "Value", sortable: true, className: "text-right", cellClassName: "tabular text-right font-semibold", render: (r) => money(r.value) },
  ];

  const submitTx = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inventory/transactions", { item_id: stockItem.id, ...tx });
      toast.success("Stock updated");
      setStockItem(null);
      setTx({ type: "in", qty: "", reason: "", reference: "" });
      setRefreshKey((k) => k + 1);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const reasonOptions = tx.type === "wastage" ? options("wastage_reason") : tx.type === "adjustment" ? options("adjustment_reason") : [];

  return (
    <>
      <ResourcePage
        key={refreshKey}
        title="Inventory"
        subtitle="Items, stock levels, valuation and movement. Bulk select for mass actions."
        endpoint="items"
        module="inventory"
        columns={columns}
        fields={fields}
        searchPlaceholder="Search items, SKU…"
        emptyHint="Add items to start tracking stock."
        defaultParams={{ _r: refreshKey }}
        rowActionsExtra={can("inventory:edit") ? (r) => (
          <DropdownMenuItem data-testid={`stock-move-${r.id}`} onClick={() => setStockItem(r)}>
            <ArrowLeftRight className="mr-2 h-4 w-4" /> Stock movement
          </DropdownMenuItem>
        ) : undefined}
      />

      <Dialog open={!!stockItem} onOpenChange={(v) => !v && setStockItem(null)}>
        <DialogContent data-testid="stock-dialog">
          <DialogHeader><DialogTitle className="font-display">Stock movement — {stockItem?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitTx} className="space-y-4">
            <div className="rounded-sm border border-border bg-secondary/50 p-3 text-sm">
              Current stock: <span className="font-semibold tabular">{num(stockItem?.current_stock)} {stockItem?.unit}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="eyebrow">Type</Label>
                <Select value={tx.type} onValueChange={(v) => setTx((s) => ({ ...s, type: v }))}>
                  <SelectTrigger data-testid="tx-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Stock In</SelectItem>
                    <SelectItem value="out">Stock Out</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                    <SelectItem value="wastage">Wastage</SelectItem>
                    <SelectItem value="count">Physical Count</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Quantity</Label>
                <Input data-testid="tx-qty" type="number" step="0.01" value={tx.qty} onChange={(e) => setTx((s) => ({ ...s, qty: e.target.value }))} required />
              </div>
            </div>
            {reasonOptions.length > 0 && (
              <div className="space-y-1.5">
                <Label className="eyebrow">Reason</Label>
                <Select value={tx.reason} onValueChange={(v) => setTx((s) => ({ ...s, reason: v }))}>
                  <SelectTrigger data-testid="tx-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>{reasonOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="eyebrow">Reference</Label>
              <Input data-testid="tx-ref" value={tx.reference} onChange={(e) => setTx((s) => ({ ...s, reference: e.target.value }))} placeholder="Optional note / doc no." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStockItem(null)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="tx-submit">{busy ? "Saving…" : "Apply movement"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
